import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaEye, 
  FaEyeSlash, 
  FaLock, 
  FaShieldAlt, 
  FaCheckCircle,
  FaTimesCircle,
  FaKey,
  FaArrowRight,
  FaArrowLeft
} from "react-icons/fa";
import { FiShield, FiLock, FiUnlock, FiAlertCircle, FiCheck } from "react-icons/fi";
import Swal from 'sweetalert2';

const UpdateLoginPassword = () => {
  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm();
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordChecks(checks);
    
    const strength = Object.values(checks).filter(Boolean).length;
    setPasswordStrength(strength);
    return strength;
  };

  const onSubmit = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          title: 'Password Updated!',
          text: 'Your password has been changed successfully.',
          icon: 'success',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#6366f1',
          background: '#ffffff',
          timer: 2000,
          timerProgressBar: true
        });
        reset();
        setPasswordStrength(0);
        setPasswordChecks({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });
      } else {
        Swal.fire({
          title: 'Update Failed',
          text: result.message || 'Failed to update password',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#ef4444',
          background: '#ffffff'
        });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while updating password',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ef4444'
      });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    if (passwordStrength <= 4) return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  const getStrengthText = () => {
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 3) return 'Fair';
    if (passwordStrength <= 4) return 'Good';
    return 'Strong';
  };

  const inputClasses = "w-full px-4 py-3 text-base rounded-xl border transition-all duration-200 outline-none pr-12 bg-gray-50/50 focus:bg-white";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-lg">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg mb-4">
            <FaLock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Update Password
          </h1>
          <p className="text-gray-500 mt-2 text-sm">Change your login password to keep your account secure</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Form Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaKey className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">Security Settings</h2>
                <p className="text-indigo-100 text-sm">Choose a strong password</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Current Password Field */}
            <div>
              <label className={labelClasses}>
                <span className="flex items-center gap-2">
                  <FiLock className="w-4 h-4 text-indigo-500" />
                  Current Password
                </span>
              </label>
              <div className="relative group">
                <input
                  type={showPassword.current ? "text" : "password"}
                  {...register("currentPassword", { required: "Current password is required" })}
                  className={`${inputClasses} ${errors.currentPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-indigo-500'}`}
                  placeholder="Enter your current password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  {showPassword.current ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.currentPassword && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-500 flex items-center gap-1"
                >
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.currentPassword.message}
                </motion.p>
              )}
            </div>

            {/* New Password Field */}
            <div>
              <label className={labelClasses}>
                <span className="flex items-center gap-2">
                  <FiUnlock className="w-4 h-4 text-indigo-500" />
                  New Password
                </span>
              </label>
              <div className="relative group">
                <input
                  type={showPassword.new ? "text" : "password"}
                  {...register("newPassword", {
                    required: "New password is required",
                    minLength: { value: 8, message: "Password must be at least 8 characters" },
                    onChange: (e) => checkPasswordStrength(e.target.value)
                  })}
                  className={`${inputClasses} ${errors.newPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-indigo-500'}`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  {showPassword.new ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-500 flex items-center gap-1"
                >
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.newPassword.message}
                </motion.p>
              )}

              {/* Password Strength Indicator */}
              {watch('newPassword') && watch('newPassword').length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(passwordStrength / 5) * 100}%` }}
                        className={`h-full ${getStrengthColor()} rounded-full transition-all duration-300`}
                      />
                    </div>
                    <span className={`ml-3 text-xs font-semibold ${getStrengthColor().replace('bg-', 'text-')}`}>
                      {getStrengthText()}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`flex items-center gap-1 ${passwordChecks.length ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {passwordChecks.length ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      <span>8+ characters</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {passwordChecks.uppercase ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      <span>Uppercase letter</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {passwordChecks.lowercase ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      <span>Lowercase letter</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordChecks.number ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {passwordChecks.number ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      <span>Number</span>
                    </div>
                    <div className={`flex items-center gap-1 col-span-2 ${passwordChecks.special ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {passwordChecks.special ? <FiCheck className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
                      <span>Special character (!@#$%^&*)</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className={labelClasses}>
                <span className="flex items-center gap-2">
                  <FiShield className="w-4 h-4 text-indigo-500" />
                  Confirm New Password
                </span>
              </label>
              <div className="relative group">
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: value => value === watch('newPassword') || "Passwords do not match"
                  })}
                  className={`${inputClasses} ${errors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-indigo-500'}`}
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  {showPassword.confirm ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm text-red-500 flex items-center gap-1"
                >
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.confirmPassword.message}
                </motion.p>
              )}
              {watch('confirmPassword') && watch('newPassword') && watch('confirmPassword') === watch('newPassword') && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-1 text-sm text-emerald-600 flex items-center gap-1"
                >
                  <FiCheck className="w-3 h-3" />
                  Passwords match!
                </motion.p>
              )}
            </div>

            {/* Password Tips */}
            <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <div className="flex items-start gap-3">
                <FaShieldAlt className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-indigo-800">Password Tips</p>
                  <ul className="text-xs text-indigo-700 mt-2 space-y-1">
                    <li>• Use at least 8 characters</li>
                    <li>• Include uppercase and lowercase letters</li>
                    <li>• Add numbers and special characters</li>
                    <li>• Avoid using common words or personal information</li>
                    <li>• Don't reuse passwords from other accounts</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold 
                transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl
                disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Updating Password...
                  </>
                ) : (
                  <>
                    <FaArrowRight className="w-4 h-4" />
                    Update Password
                  </>
                )}
              </motion.button>
            </div>

            {/* Security Note */}
            <div className="text-center">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                Your password is encrypted and securely stored
              </p>
            </div>
          </form>
        </motion.div>

        {/* Back to Dashboard Link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => window.history.back()}
            className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 mx-auto"
          >
            <FaArrowLeft className="w-3 h-3" />
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateLoginPassword;